import React, { useState, useEffect, useCallback } from 'react';
import NetworkStatus from './NetworkStatus';
import ReceiptEditor from './ReceiptEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReceiptRepository = () => {
  const [receipts, setReceipts] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(null);
  const [showEditor, setShowEditor] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [approving, setApproving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [availableRecords, setAvailableRecords] = useState({ expenses: [], income: [] });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 1
  });
  const [totalPages, setTotalPages] = useState(1);
  const [enhancing, setEnhancing] = useState(false);

  const loadReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: filters.page,
        limit: 12
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/repository?${params}`);
      const data = await response.json();
      if (data.success) {
        setReceipts(data.receipts);
        setTotalPages(data.total_pages);
      }
    } catch (error) {
      console.error('Error loading receipts:', error);
    }
    setLoading(false);
  }, [filters]);

  const loadStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPendingReceipts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/pending`);
      const data = await response.json();
      if (data.success) {
        setPendingReceipts(data.receipts);
      }
    } catch (error) {
      console.error('Error loading pending receipts:', error);
    }
  };

  const handleApproveReceipt = async (receiptId, recordType, notes = '') => {
    setApproving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/${receiptId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'approve',
          record_type: recordType,
          notes: notes
        })
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Receipt approved! ${recordType.charAt(0).toUpperCase() + recordType.slice(1)} record created.`);
        loadReceipts();
        loadStats();
        loadPendingReceipts();
        setShowApprovalModal(null);
      } else {
        alert('Error: ' + (data.detail || 'Failed to approve receipt'));
      }
    } catch (error) {
      console.error('Error approving receipt:', error);
      alert('Network error while approving receipt');
    }
    setApproving(false);
  };

  const handleRejectReceipt = async (receiptId, notes = '') => {
    if (!window.confirm('Are you sure you want to reject this receipt?')) return;
    
    setApproving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/${receiptId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reject',
          notes: notes
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('Receipt rejected');
        loadReceipts();
        loadStats();
        loadPendingReceipts();
        setShowApprovalModal(null);
      } else {
        alert('Error: ' + (data.detail || 'Failed to reject receipt'));
      }
    } catch (error) {
      console.error('Error rejecting receipt:', error);
      alert('Network error while rejecting receipt');
    }
    setApproving(false);
  };

  const loadAvailableRecords = async () => {
    try {
      // Load recent expenses without receipts
      const expensesRes = await fetch(`${BACKEND_URL}/api/finance/expenses?limit=50`);
      const expensesData = await expensesRes.json();
      
      // Load recent income without receipts
      const incomeRes = await fetch(`${BACKEND_URL}/api/finance/income?limit=50`);
      const incomeData = await incomeRes.json();
      
      setAvailableRecords({
        expenses: (expensesData.expenses || []).filter(e => !e.attachment_url),
        income: (incomeData.income || []).filter(i => !i.receipt_url)
      });
    } catch (error) {
      console.error('Error loading available records:', error);
    }
  };

  const handleOpenLinkModal = (receipt) => {
    setShowLinkModal(receipt);
    loadAvailableRecords();
  };

  const handleLinkReceipt = async (receiptId, recordId, recordType) => {
    setLinking(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/link/${receiptId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: recordId,
          record_type: recordType
        })
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Receipt linked successfully!');
        loadReceipts();
        loadStats();
        setShowLinkModal(null);
        setShowPreviewModal(null);
      } else {
        alert('Error: ' + (data.detail || 'Failed to link receipt'));
      }
    } catch (error) {
      console.error('Error linking receipt:', error);
      alert('Network error while linking receipt');
    }
    setLinking(false);
  };

  useEffect(() => {
    loadReceipts();
    loadStats();
    loadPendingReceipts();
  }, [loadReceipts]);

  const handleScanReceipt = async (file) => {
    setScanning(true);
    setScanResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/scan`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        setScanResult({
          success: true,
          receipt: data.receipt,
          extractedData: data.extracted_data
        });
        loadReceipts();
        loadStats();
      } else {
        setScanResult({
          success: false,
          error: data.error || 'Failed to scan receipt'
        });
      }
    } catch (error) {
      setScanResult({
        success: false,
        error: 'Network error while scanning receipt'
      });
    }
    setScanning(false);
  };

  const handleEnhanceImage = async (file, autoEnhance = true) => {
    setEnhancing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/enhance?auto_enhance=${autoEnhance}`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        return data.enhanced_url;
      }
    } catch (error) {
      console.error('Enhancement error:', error);
    }
    setEnhancing(false);
    return null;
  };

  const handleSaveEditedReceipt = async (blob, settings) => {
    // Upload the edited image and optionally re-scan
    const formData = new FormData();
    formData.append('file', blob, 'edited_receipt.jpg');

    try {
      // Upload as new scan
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/scan`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      
      if (data.success) {
        // Close editor and refresh
        setShowEditor(null);
        loadReceipts();
        loadStats();
        alert('✅ Edited receipt saved and scanned successfully!');
      } else {
        alert('Failed to save edited receipt: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving edited receipt:', error);
      alert('Failed to save edited receipt');
    }
  };

  const handleDeleteReceipt = async (receiptId) => {
    if (!window.confirm('Are you sure you want to delete this receipt?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/finance/receipts/delete/${receiptId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        loadReceipts();
        loadStats();
        setShowPreviewModal(null);
      }
    } catch (error) {
      console.error('Error deleting receipt:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Scan Modal Component - Enhanced with Multiple Receipts & Manual Correction
  const ScanModal = () => {
    const [dragActive, setDragActive] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [currentScanIndex, setCurrentScanIndex] = useState(0);
    const [allScanResults, setAllScanResults] = useState([]);
    const [showManualEdit, setShowManualEdit] = useState(false);
    const [editableData, setEditableData] = useState({
      vendor_name: '',
      date: '',
      total_amount: '',
      currency: 'USD',
      payment_method: '',
      category_suggestion: '',
      tax_amount: '',
      notes: ''
    });

    // Supported file types - expanded
    const supportedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 
      'image/bmp', 'image/tiff', 'image/heic', 'image/heif',
      'application/pdf'
    ];
    const supportedExtensions = '.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.heic,.heif,.pdf';

    const handleDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === "dragenter" || e.type === "dragover") {
        setDragActive(true);
      } else if (e.type === "dragleave") {
        setDragActive(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFilesSelect(Array.from(e.dataTransfer.files));
      }
    };

    const handleFilesSelect = (files) => {
      const validFiles = files.filter(file => {
        const isValidType = supportedTypes.includes(file.type) || 
                           file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp|tiff|heic|heif|pdf)$/);
        if (!isValidType) {
          console.log(`Skipping unsupported file: ${file.name}`);
        }
        return isValidType;
      });

      if (validFiles.length === 0) {
        alert('Please select supported file types: Images (JPG, PNG, GIF, WebP, BMP, TIFF, HEIC) or PDF');
        return;
      }

      // Add to existing files
      const newFiles = [...selectedFiles, ...validFiles];
      const newPreviews = validFiles.map(file => {
        if (file.type === 'application/pdf') {
          return { url: null, isPdf: true, name: file.name };
        }
        return { url: URL.createObjectURL(file), isPdf: false, name: file.name };
      });
      
      setSelectedFiles(newFiles);
      setPreviewUrls([...previewUrls, ...newPreviews]);
      setScanResult(null);
      setAllScanResults([]);
    };

    const removeFile = (index) => {
      const newFiles = selectedFiles.filter((_, i) => i !== index);
      const newPreviews = previewUrls.filter((_, i) => i !== index);
      setSelectedFiles(newFiles);
      setPreviewUrls(newPreviews);
      setAllScanResults(allScanResults.filter((_, i) => i !== index));
    };

    const handleScanAll = async () => {
      if (selectedFiles.length === 0) return;
      
      setScanning(true);
      const results = [];
      
      for (let i = 0; i < selectedFiles.length; i++) {
        setCurrentScanIndex(i);
        try {
          const formData = new FormData();
          formData.append('file', selectedFiles[i]);

          const response = await fetch(`${BACKEND_URL}/api/finance/receipts/scan`, {
            method: 'POST',
            body: formData
          });
          const data = await response.json();
          
          results.push({
            success: data.success,
            filename: selectedFiles[i].name,
            receipt: data.receipt,
            extractedData: data.extracted_data,
            error: data.error
          });
        } catch (error) {
          results.push({
            success: false,
            filename: selectedFiles[i].name,
            error: 'Network error while scanning'
          });
        }
      }
      
      setAllScanResults(results);
      setScanning(false);
      
      // If we have successful scans, pre-fill editable data from first result
      const firstSuccess = results.find(r => r.success && r.extractedData);
      if (firstSuccess) {
        setEditableData({
          vendor_name: firstSuccess.extractedData.vendor_name || '',
          date: firstSuccess.extractedData.date || '',
          total_amount: firstSuccess.extractedData.total_amount?.toString() || '',
          currency: firstSuccess.extractedData.currency || 'USD',
          payment_method: firstSuccess.extractedData.payment_method || '',
          category_suggestion: firstSuccess.extractedData.category_suggestion || '',
          tax_amount: firstSuccess.extractedData.tax_amount?.toString() || '',
          notes: firstSuccess.extractedData.notes || ''
        });
      }
      
      loadReceipts();
      loadStats();
    };

    const handleSaveWithCorrections = async () => {
      // Save the manual corrections to all scanned receipts
      for (const result of allScanResults) {
        if (result.success && result.receipt?.id) {
          try {
            await fetch(`${BACKEND_URL}/api/finance/receipts/${result.receipt.id}/update-data`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                extracted_data: {
                  ...result.extractedData,
                  vendor_name: editableData.vendor_name || result.extractedData?.vendor_name,
                  date: editableData.date || result.extractedData?.date,
                  total_amount: parseFloat(editableData.total_amount) || result.extractedData?.total_amount,
                  currency: editableData.currency || result.extractedData?.currency,
                  payment_method: editableData.payment_method || result.extractedData?.payment_method,
                  category_suggestion: editableData.category_suggestion || result.extractedData?.category_suggestion,
                  tax_amount: editableData.tax_amount ? parseFloat(editableData.tax_amount) : null,
                  notes: editableData.notes,
                  manually_corrected: true
                }
              })
            });
          } catch (error) {
            console.error('Error saving corrections:', error);
          }
        }
      }
      
      alert('✅ Receipts saved with your corrections!');
      loadReceipts();
      setShowScanModal(false);
      setScanResult(null);
    };

    const categories = [
      'Salaries', 'Purchases/Supplies', 'Travel/Transport', 'Fuel',
      'Maintenance/Repairs', 'Internet/Starlink', 'Utilities', 'Training',
      'Projects', 'Office Supplies', 'Equipment', 'Other'
    ];

    const paymentMethods = ['Cash', 'Card', 'Mobile Money', 'Bank Transfer', 'Check', 'Other'];
    const currencies = ['USD', 'EUR', 'LRD', 'GNF', 'SLL', 'GBP', 'KES'];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex justify-between items-center sticky top-0 z-10">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                🤖 AI Receipt Scanner
              </h2>
              <p className="text-purple-200 text-sm">Upload multiple receipts • All formats supported • Manual corrections</p>
            </div>
            <button 
              onClick={() => {
                setShowScanModal(false);
                setScanResult(null);
                setSelectedFiles([]);
                setPreviewUrls([]);
                setAllScanResults([]);
              }} 
              className="text-white hover:text-gray-200 text-2xl"
            >
              ✕
            </button>
          </div>

          <div className="p-6">
            {/* Upload Area */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800">📤 Upload Receipts</h3>
                {selectedFiles.length > 0 && (
                  <span className="text-sm text-purple-600 font-medium">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                  dragActive 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-300 hover:border-purple-400'
                }`}
              >
                <div className="text-4xl mb-3">📸</div>
                <p className="text-gray-600 mb-2">Drag & drop receipt images here</p>
                <p className="text-gray-400 text-sm mb-3">Upload multiple files at once</p>
                <label className="cursor-pointer">
                  <span className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                    Browse Files
                  </span>
                  <input
                    type="file"
                    accept={supportedExtensions}
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files && handleFilesSelect(Array.from(e.target.files))}
                  />
                </label>
                <p className="text-xs text-gray-400 mt-3">
                  Supports: JPG, PNG, GIF, WebP, BMP, TIFF, HEIC, PDF (Max 10MB each)
                </p>
              </div>

              {/* File Previews */}
              {previewUrls.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Selected Files:</p>
                  <div className="flex flex-wrap gap-3">
                    {previewUrls.map((preview, index) => (
                      <div key={index} className="relative group">
                        {preview.isPdf ? (
                          <div className="w-20 h-20 bg-red-50 rounded-lg flex flex-col items-center justify-center border border-red-200">
                            <span className="text-2xl">📄</span>
                            <span className="text-xs text-red-600 mt-1">PDF</span>
                          </div>
                        ) : (
                          <img 
                            src={preview.url} 
                            alt={`Receipt ${index + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border"
                          />
                        )}
                        <button
                          onClick={() => removeFile(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          ✕
                        </button>
                        {allScanResults[index] && (
                          <div className={`absolute bottom-0 left-0 right-0 text-center text-xs py-0.5 rounded-b-lg ${
                            allScanResults[index].success ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                          }`}>
                            {allScanResults[index].success ? '✓' : '✗'}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Add More Button */}
                    <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                      <span className="text-2xl text-gray-400">+</span>
                      <span className="text-xs text-gray-400">Add More</span>
                      <input
                        type="file"
                        accept={supportedExtensions}
                        multiple
                        className="hidden"
                        onChange={(e) => e.target.files && handleFilesSelect(Array.from(e.target.files))}
                      />
                    </label>
                  </div>
                </div>
              )}

              {/* Scan Button */}
              {selectedFiles.length > 0 && (
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleScanAll}
                    disabled={scanning}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                  >
                    {scanning ? (
                      <>
                        <span className="animate-spin">⚡</span>
                        Scanning {currentScanIndex + 1} of {selectedFiles.length}...
                      </>
                    ) : (
                      <>
                        🤖 Scan All Receipts ({selectedFiles.length})
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowScanModal(false);
                      if (previewUrls[0] && !previewUrls[0].isPdf) {
                        setShowEditor(previewUrls[0].url);
                      }
                    }}
                    disabled={previewUrls[0]?.isPdf}
                    className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                    title={previewUrls[0]?.isPdf ? 'Editor not available for PDF' : 'Edit first image'}
                  >
                    🖌️ Edit
                  </button>
                </div>
              )}
            </div>

            {/* Results Area */}
            {(scanning || allScanResults.length > 0) && (
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800">📊 Scan Results</h3>
                  {allScanResults.length > 0 && (
                    <button
                      onClick={() => setShowManualEdit(!showManualEdit)}
                      className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                    >
                      ✏️ {showManualEdit ? 'Hide' : 'Edit'} Data Manually
                    </button>
                  )}
                </div>

                {scanning ? (
                  <div className="bg-purple-50 rounded-xl p-8 text-center">
                    <div className="animate-pulse">
                      <div className="text-5xl mb-4">🔍</div>
                      <p className="text-purple-700 font-medium">
                        Scanning receipt {currentScanIndex + 1} of {selectedFiles.length}...
                      </p>
                      <p className="text-purple-500 text-sm mt-2">
                        {selectedFiles[currentScanIndex]?.name}
                      </p>
                      <div className="mt-4 w-full bg-purple-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all"
                          style={{ width: `${((currentScanIndex + 1) / selectedFiles.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : allScanResults.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-green-50 rounded-xl p-4 text-center border border-green-200">
                        <div className="text-2xl font-bold text-green-600">
                          {allScanResults.filter(r => r.success).length}
                        </div>
                        <div className="text-sm text-green-700">Successful</div>
                      </div>
                      <div className="bg-red-50 rounded-xl p-4 text-center border border-red-200">
                        <div className="text-2xl font-bold text-red-600">
                          {allScanResults.filter(r => !r.success).length}
                        </div>
                        <div className="text-sm text-red-700">Failed</div>
                      </div>
                      <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">
                          {allScanResults.filter(r => r.extractedData?.total_amount).reduce((sum, r) => sum + (r.extractedData?.total_amount || 0), 0).toFixed(2)}
                        </div>
                        <div className="text-sm text-blue-700">Total Amount</div>
                      </div>
                    </div>

                    {/* Manual Edit Form */}
                    {showManualEdit && (
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                          ✏️ Correct/Edit Extracted Data
                          <span className="text-xs font-normal text-amber-600">(These values will be applied to all receipts)</span>
                        </h4>
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Vendor Name</label>
                            <input
                              type="text"
                              value={editableData.vendor_name}
                              onChange={(e) => setEditableData({...editableData, vendor_name: e.target.value})}
                              placeholder="Enter vendor name"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Date</label>
                            <input
                              type="date"
                              value={editableData.date}
                              onChange={(e) => setEditableData({...editableData, date: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Total Amount</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editableData.total_amount}
                              onChange={(e) => setEditableData({...editableData, total_amount: e.target.value})}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Currency</label>
                            <select
                              value={editableData.currency}
                              onChange={(e) => setEditableData({...editableData, currency: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
                            >
                              {currencies.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Payment Method</label>
                            <select
                              value={editableData.payment_method}
                              onChange={(e) => setEditableData({...editableData, payment_method: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
                            >
                              <option value="">Select...</option>
                              {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Category</label>
                            <select
                              value={editableData.category_suggestion}
                              onChange={(e) => setEditableData({...editableData, category_suggestion: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-white"
                            >
                              <option value="">Select...</option>
                              {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 block mb-1">Tax Amount</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editableData.tax_amount}
                              onChange={(e) => setEditableData({...editableData, tax_amount: e.target.value})}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs text-gray-600 block mb-1">Notes</label>
                            <input
                              type="text"
                              value={editableData.notes}
                              onChange={(e) => setEditableData({...editableData, notes: e.target.value})}
                              placeholder="Additional notes..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            />
                          </div>
                        </div>

                        <button
                          onClick={handleSaveWithCorrections}
                          className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium text-sm"
                        >
                          💾 Save Corrections
                        </button>
                      </div>
                    )}

                    {/* Individual Results */}
                    <div className="space-y-2">
                      {allScanResults.map((result, index) => (
                        <div 
                          key={index}
                          className={`rounded-lg p-3 border ${
                            result.success 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-xl">{result.success ? '✅' : '❌'}</span>
                              <div>
                                <p className="font-medium text-gray-800 text-sm">{result.filename}</p>
                                {result.success && result.extractedData && (
                                  <p className="text-xs text-gray-500">
                                    {result.extractedData.vendor_name || 'Unknown Vendor'} • 
                                    {result.extractedData.currency} {result.extractedData.total_amount?.toFixed(2) || '0.00'} • 
                                    {result.extractedData.date || 'No date'}
                                  </p>
                                )}
                                {!result.success && (
                                  <p className="text-xs text-red-600">{result.error}</p>
                                )}
                              </div>
                            </div>
                            {result.success && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                (result.extractedData?.confidence_score || 0) >= 0.8 
                                  ? 'bg-green-200 text-green-700' 
                                  : 'bg-amber-200 text-amber-700'
                              }`}>
                                {((result.extractedData?.confidence_score || 0) * 100).toFixed(0)}% conf.
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Next Steps */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <p className="text-sm text-blue-700">
                        💡 <strong>Next Step:</strong> Go to "Add Expense" or "Add Income" to create records using this data.
                        All receipts have been saved to your repository.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Preview Modal
  const PreviewModal = () => {
    if (!showPreviewModal) return null;
    const receipt = showPreviewModal;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-gradient-to-r from-gray-700 to-gray-900 px-6 py-4 rounded-t-2xl flex justify-between items-center">
            <h2 className="text-lg font-bold text-white">Receipt Details</h2>
            <button onClick={() => setShowPreviewModal(null)} className="text-white hover:text-gray-300 text-xl">✕</button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image Preview */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📷 Receipt Image</h3>
                <div className="bg-gray-100 rounded-xl p-2">
                  <img 
                    src={`${BACKEND_URL}${receipt.file_url}`}
                    alt="Receipt"
                    className="w-full rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><text y=".9em" font-size="90">📄</text></svg>';
                    }}
                  />
                </div>
                <div className="mt-3 flex gap-2">
                  <a 
                    href={`${BACKEND_URL}${receipt.file_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-center text-sm hover:bg-blue-700"
                  >
                    🔍 View Full Size
                  </a>
                  <a 
                    href={`${BACKEND_URL}${receipt.file_url}`}
                    download
                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg text-center text-sm hover:bg-green-700"
                  >
                    ⬇️ Download
                  </a>
                </div>
                <button
                  onClick={() => {
                    setShowPreviewModal(null);
                    setShowEditor(`${BACKEND_URL}${receipt.file_url}`);
                  }}
                  className="w-full mt-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-center text-sm hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  🖌️ Edit with Advanced Editor
                </button>
              </div>

              {/* Extracted Data */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">📊 Extracted Data</h3>
                {receipt.extracted_data ? (
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-gray-500 text-xs">Vendor</p>
                        <p className="font-medium">{receipt.extracted_data.vendor_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Date</p>
                        <p className="font-medium">{receipt.extracted_data.date || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Amount</p>
                        <p className="font-bold text-green-600">
                          {receipt.extracted_data.currency} {receipt.extracted_data.total_amount?.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Payment</p>
                        <p className="font-medium">{receipt.extracted_data.payment_method || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Category</p>
                        <p className="font-medium">{receipt.extracted_data.category_suggestion || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs">Confidence</p>
                        <p className="font-medium">{((receipt.confidence_score || 0) * 100).toFixed(0)}%</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No extracted data available</p>
                )}

                {/* Metadata */}
                <div className="mt-4 bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">📁 File Info</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>Original: {receipt.original_filename}</p>
                    <p>Size: {formatFileSize(receipt.file_size)}</p>
                    <p>Scanned: {formatDate(receipt.scanned_at)}</p>
                    <p>Status: 
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                        receipt.status === 'linked' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {receipt.status}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 space-y-2">
                  {receipt.status !== 'linked' && receipt.status !== 'approved' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenLinkModal(receipt);
                      }}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                    >
                      🔗 Link to Record
                    </button>
                  )}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteReceipt(receipt.id);
                    }}
                    className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                  >
                    🗑️ Delete Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            🧾 Receipt Repository
          </h1>
          <p className="text-gray-500">Scan, store, and manage all your receipts with AI</p>
        </div>
        <button
          onClick={() => setShowScanModal(true)}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 font-medium shadow-lg shadow-purple-200 flex items-center gap-2"
        >
          🤖 Scan New Receipt
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-gray-500 text-sm">Total Receipts</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total_receipts}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-gray-500 text-sm">Pending Review</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending_receipts}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-gray-500 text-sm">Linked to Records</p>
            <p className="text-2xl font-bold text-green-600">{stats.linked_receipts}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <p className="text-gray-500 text-sm">Storage Used</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_storage_mb} MB</p>
          </div>
        </div>
      )}

      {/* Pending Approval Banner */}
      {pendingReceipts.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <h3 className="font-bold text-amber-800">
                  {pendingReceipts.length} Receipt{pendingReceipts.length > 1 ? 's' : ''} Pending Approval
                </h3>
                <p className="text-amber-600 text-sm">Submitted via Mobile Scanner - Review and approve to create records</p>
              </div>
            </div>
            <button
              onClick={() => setFilters({ ...filters, status: 'pending', page: 1 })}
              className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium"
            >
              View All Pending
            </button>
          </div>
          
          {/* Quick Preview of Pending Receipts */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {pendingReceipts.slice(0, 3).map((receipt) => (
              <div key={receipt.id} className="bg-white rounded-lg p-3 border border-amber-200 flex gap-3">
                {receipt.file_url ? (
                  <img 
                    src={`${BACKEND_URL}${receipt.file_url}`}
                    alt="Receipt"
                    className="w-16 h-16 object-cover rounded-lg"
                    onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="40">📄</text></svg>'; }}
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">📄</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 text-sm truncate">
                    {receipt.extracted_data?.vendor_name || receipt.submitter_name || 'Unknown'}
                  </p>
                  <p className="text-green-600 font-bold text-sm">
                    {receipt.extracted_data?.currency || 'USD'} {(receipt.extracted_data?.total_amount || 0).toFixed(2)}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {receipt.scanned_at ? new Date(receipt.scanned_at).toLocaleDateString() : 'No date'}
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setShowApprovalModal(receipt)}
                    className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => handleRejectReceipt(receipt.id)}
                    className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs hover:bg-red-200"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {pendingReceipts.length > 3 && (
            <p className="text-amber-600 text-sm mt-2 text-center">
              +{pendingReceipts.length - 3} more pending receipts
            </p>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="🔍 Search by vendor name..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">All Status</option>
          <option value="pending">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="linked">Linked</option>
          <option value="rejected">Rejected</option>
          <option value="archived">Archived</option>
        </select>
        <button
          onClick={() => {
            setFilters({ status: '', search: '', page: 1 });
          }}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Clear Filters
        </button>
      </div>

      {/* Receipt Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500">Loading receipts...</p>
          </div>
        </div>
      ) : receipts.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="text-6xl mb-4">🧾</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Receipts Yet</h3>
          <p className="text-gray-500 mb-6">Start by scanning your first receipt with AI</p>
          <button
            onClick={() => setShowScanModal(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            🤖 Scan Your First Receipt
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {receipts.map((receipt) => (
              <div 
                key={receipt.id}
                onClick={() => setShowPreviewModal(receipt)}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer hover:shadow-md transition-shadow group"
              >
                {/* Thumbnail */}
                <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
                  <img
                    src={receipt.thumbnail_url ? `${BACKEND_URL}${receipt.thumbnail_url}` : `${BACKEND_URL}${receipt.file_url}`}
                    alt="Receipt thumbnail"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="55" font-size="40" text-anchor="middle">📄</text></svg>';
                    }}
                  />
                  <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    receipt.status === 'linked' 
                      ? 'bg-green-500 text-white' 
                      : 'bg-amber-500 text-white'
                  }`}>
                    {receipt.status}
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="font-medium text-gray-800 truncate text-sm">
                    {receipt.extracted_data?.vendor_name || receipt.original_filename}
                  </p>
                  {receipt.extracted_data?.total_amount && (
                    <p className="text-green-600 font-bold">
                      {receipt.extracted_data.currency || '$'} {receipt.extracted_data.total_amount.toFixed(2)}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(receipt.scanned_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="px-4 py-2 bg-white border rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                ← Previous
              </button>
              <span className="px-4 py-2 bg-gray-100 rounded-lg">
                Page {filters.page} of {totalPages}
              </span>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page === totalPages}
                className="px-4 py-2 bg-white border rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showScanModal && <ScanModal />}
      {showPreviewModal && <PreviewModal />}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">✅ Approve Receipt</h2>
                <p className="text-green-200 text-sm">Review and approve this submission</p>
              </div>
              <button 
                onClick={() => setShowApprovalModal(null)} 
                className="text-white hover:text-gray-200 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {/* Receipt Preview */}
              <div className="flex gap-4 mb-4">
                {showApprovalModal.file_url ? (
                  <img 
                    src={`${BACKEND_URL}${showApprovalModal.file_url}`}
                    alt="Receipt"
                    className="w-24 h-24 object-cover rounded-lg border"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center text-3xl">📄</div>
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800">
                    {showApprovalModal.extracted_data?.vendor_name || 'Unknown Vendor'}
                  </h3>
                  <p className="text-2xl font-bold text-green-600">
                    {showApprovalModal.extracted_data?.currency || 'USD'} {(showApprovalModal.extracted_data?.total_amount || 0).toFixed(2)}
                  </p>
                  <p className="text-gray-500 text-sm">
                    Date: {showApprovalModal.extracted_data?.date || 'N/A'}
                  </p>
                  {showApprovalModal.submitter_name && (
                    <p className="text-gray-400 text-xs mt-1">
                      Submitted by: {showApprovalModal.submitter_name}
                    </p>
                  )}
                </div>
              </div>

              {/* Extracted Data Summary */}
              {showApprovalModal.extracted_data && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <h4 className="font-medium text-gray-700 mb-2 text-sm">📊 Extracted Data</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <span className="ml-2 font-medium">{showApprovalModal.extracted_data.category_suggestion || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Payment:</span>
                      <span className="ml-2 font-medium">{showApprovalModal.extracted_data.payment_method || 'N/A'}</span>
                    </div>
                    {showApprovalModal.extracted_data.confidence_score && (
                      <div>
                        <span className="text-gray-500">Confidence:</span>
                        <span className="ml-2 font-medium">{(showApprovalModal.extracted_data.confidence_score * 100).toFixed(0)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Record Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Create as:</label>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleApproveReceipt(showApprovalModal.id, 'expense')}
                    disabled={approving}
                    className="flex-1 py-3 bg-red-100 text-red-700 rounded-xl font-medium hover:bg-red-200 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {approving ? '...' : '💸'} Expense
                  </button>
                  <button
                    onClick={() => handleApproveReceipt(showApprovalModal.id, 'income')}
                    disabled={approving}
                    className="flex-1 py-3 bg-green-100 text-green-700 rounded-xl font-medium hover:bg-green-200 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {approving ? '...' : '💰'} Income
                  </button>
                </div>
              </div>

              {/* Reject Option */}
              <div className="border-t pt-4">
                <button
                  onClick={() => handleRejectReceipt(showApprovalModal.id)}
                  disabled={approving}
                  className="w-full py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  ❌ Reject Receipt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Link to Record Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white">🔗 Link Receipt to Record</h2>
                <p className="text-purple-200 text-sm">Select an existing expense or income record</p>
              </div>
              <button 
                onClick={() => setShowLinkModal(null)} 
                className="text-white hover:text-gray-200 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Receipt Preview */}
              <div className="flex gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                {showLinkModal.file_url ? (
                  <img 
                    src={`${BACKEND_URL}${showLinkModal.file_url}`}
                    alt="Receipt"
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center text-2xl">📄</div>
                )}
                <div>
                  <h3 className="font-bold text-gray-800">
                    {showLinkModal.extracted_data?.vendor_name || showLinkModal.original_filename}
                  </h3>
                  <p className="text-green-600 font-bold">
                    {showLinkModal.extracted_data?.currency || 'USD'} {(showLinkModal.extracted_data?.total_amount || 0).toFixed(2)}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {showLinkModal.extracted_data?.date || 'No date'}
                  </p>
                </div>
              </div>

              {/* Expenses Section */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  💸 Recent Expenses <span className="text-gray-400 font-normal text-sm">({availableRecords.expenses.length} without receipts)</span>
                </h3>
                {availableRecords.expenses.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {availableRecords.expenses.slice(0, 10).map((expense) => (
                      <div 
                        key={expense.id}
                        className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100 hover:border-red-300 cursor-pointer transition-colors"
                        onClick={() => handleLinkReceipt(showLinkModal.id, expense.id, 'expense')}
                      >
                        <div>
                          <p className="font-medium text-gray-800">{expense.paid_to || expense.category}</p>
                          <p className="text-xs text-gray-500">{expense.date} • {expense.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">{expense.currency} {expense.amount?.toFixed(2)}</p>
                          <button 
                            disabled={linking}
                            className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                          >
                            {linking ? '...' : 'Link →'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">No expenses without receipts found</p>
                )}
              </div>

              {/* Income Section */}
              <div>
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  💰 Recent Income <span className="text-gray-400 font-normal text-sm">({availableRecords.income.length} without receipts)</span>
                </h3>
                {availableRecords.income.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {availableRecords.income.slice(0, 10).map((income) => (
                      <div 
                        key={income.id}
                        className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100 hover:border-green-300 cursor-pointer transition-colors"
                        onClick={() => handleLinkReceipt(showLinkModal.id, income.id, 'income')}
                      >
                        <div>
                          <p className="font-medium text-gray-800">{income.payer_name || income.source_category}</p>
                          <p className="text-xs text-gray-500">{income.date} • {income.source_category}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">{income.currency} {income.amount?.toFixed(2)}</p>
                          <button 
                            disabled={linking}
                            className="text-xs text-purple-600 hover:text-purple-700 font-medium"
                          >
                            {linking ? '...' : 'Link →'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">No income records without receipts found</p>
                )}
              </div>

              {/* Or Create New */}
              <div className="mt-6 pt-4 border-t">
                <p className="text-gray-500 text-sm text-center mb-3">Or create a new record from this receipt</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowLinkModal(null);
                      setShowApprovalModal(showLinkModal);
                    }}
                    className="flex-1 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 text-sm"
                  >
                    ➕ Create New Record
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Editor */}
      {showEditor && (
        <ReceiptEditor
          imageData={showEditor}
          onSave={handleSaveEditedReceipt}
          onCancel={() => setShowEditor(null)}
        />
      )}

      {/* Network Status Indicator - Offline Support */}
      <NetworkStatus />
    </div>
  );
};

export default ReceiptRepository;
